import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { CustomGraphLegend } from '../../../../SharedComponents/CustomGraphLegend';
import { StackedBarShape } from '../../../../SharedComponents/StackedBarShape';
import { CustomGraphTooltip } from '../../../../SharedComponents/CustomGraphTooltip';
import { GraphWrapper, type GraphWrapperProps } from '../../../../SharedComponents/GraphWrapper';
import { MonthAndYearCustomTick } from '../../../../SharedComponents/MonthAndYearCustomTick';
import { useMainLayout } from '../../../../layouts/MainPageLayout/MainLayoutContext';
import { formatLocalDate } from '../../../../../utils/localDateToDate';
import { format } from 'date-fns';
import { formatStackedNumbers } from '../../../../../utils';
import { isAllSingleFamily } from '../../../../../utils/isAllSingleFamily';
import { BuildingType } from '../OverviewReport.types';
import { getGraphTitle } from '../../../../../utils/getGraphTitle';
import { useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { emitOverviewPropertiesMarketTotalEvent } from '../../../../../analytics/events';
import { ActivePropertiesDataType } from '../OverviewReport.hooks';
import type { ActivePropertiesChartData } from '../OverviewReport.data';
import type { MfRegionPerformanceResponseDto } from '@zg-rentals/ts-customer-analytics-client';

type ActivePropertiesProps = {
  selectedRegionId: number;
  chartData: Array<ActivePropertiesChartData>;
  isRegionPerformanceLoading: boolean;
  regionPerformanceData?: Array<MfRegionPerformanceResponseDto>;
  buildingTypeFilter: BuildingType;
  setBuildingTypeFilter: (value: BuildingType) => void;
  displayType: ActivePropertiesDataType;
  setDisplayType: (value: ActivePropertiesDataType) => void;
};

export const ActiveProperties = ({
  selectedRegionId,
  chartData: rawChartData,
  isRegionPerformanceLoading,
  buildingTypeFilter,
  setBuildingTypeFilter,
  displayType,
  setDisplayType,
}: ActivePropertiesProps) => {
  const { user } = useRentalManagerUser();
  const { filteredProperties, regionsMapping, selectedFilters, isFetchingInitialData, analyticsLastUpdatedDates } =
    useMainLayout();

  const lastUpdatedDate = formatLocalDate(analyticsLastUpdatedDates?.portfolioPerformanceLastUpdated);
  const lastUpdatedMarketDate = formatLocalDate(analyticsLastUpdatedDates?.marketRentLastUpdated);
  const isAnyPropertySingleFamily = filteredProperties.some((property) => property.aggregateStats.isSingleFamily);
  const allSingleFamily = isAllSingleFamily(filteredProperties);
  const effectiveBuildingType = allSingleFamily ? BuildingType.SINGLE_FAMILY : buildingTypeFilter;
  const currentDateFormatted = format(new Date(), 'MMM yyyy');

  const chartData = rawChartData.filter(
    (data) => !(data.isCurrentMonth && displayType === ActivePropertiesDataType.MARKET_TOTAL),
  );

  const title = getGraphTitle({
    graphName: 'Active properties',
    regionsMapping,
    selectedRegionId,
    selectedFilters,
    filteredProperties,
  });

  const buildingTypeText = effectiveBuildingType === BuildingType.SINGLE_FAMILY ? 'single-family' : 'multifamily';
  const lastAvailablePortfolioData = chartData.find((data) => data.isCurrentMonth);
  const infoCards: GraphWrapperProps['infoCards'] = [];

  if (lastAvailablePortfolioData) {
    infoCards.push({
      title: 'Portfolio total',
      infoText:
        'Total active :buildingTypeText properties for your portfolio or property in the selected market, month to date.'.replace(
          ':buildingTypeText',
          buildingTypeText,
        ),
      value: lastAvailablePortfolioData['Month to date portfolio total'],
    });
  }

  const isLoading = isFetchingInitialData || isRegionPerformanceLoading;
  const lastUpdateText = `${lastUpdatedDate && lastUpdatedMarketDate ? ` Portfolio data updated ${lastUpdatedDate}, market data updated ${lastUpdatedMarketDate}.` : ''}`;

  return (
    <GraphWrapper
      graphId="active-properties"
      title={title}
      subtitle={`The number of your properties advertised on Zillow Rentals Network.${lastUpdateText}`}
      buttonGroupFilter={isAnyPropertySingleFamily && !allSingleFamily ? buildingTypeFilter : undefined}
      setButtonGroupFilter={isAnyPropertySingleFamily && !allSingleFamily ? setBuildingTypeFilter : undefined}
      radioButtonEnum={ActivePropertiesDataType}
      radioButtonFilter={displayType}
      setRadioButtonFilter={(value: ActivePropertiesDataType) => {
        if (user) emitOverviewPropertiesMarketTotalEvent({ user });
        setDisplayType(value);
      }}
      infoCards={displayType === ActivePropertiesDataType.PORTFOLIO_TOTAL ? infoCards : []}
      loading={isLoading}
    >
      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={chartData}>
          <defs>
            <pattern
              id="stripe-active-properties-portfolio"
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" fill="#B88DFC" />
              <rect width="3" height="6" fill="#891EFA" />
            </pattern>
            <pattern
              id="stripe-active-properties-market"
              patternUnits="userSpaceOnUse"
              width="6"
              height="6"
              patternTransform="rotate(45)"
            >
              <rect width="6" height="6" fill="#73D4E4" />
              <rect width="3" height="6" fill="#009AAE" />
            </pattern>
          </defs>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="timestamp" interval={0} tick={<MonthAndYearCustomTick />} />
          <YAxis tickFormatter={(value) => formatStackedNumbers(value)} />
          <Tooltip
            content={
              <CustomGraphTooltip
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (filteredPayload: Array<any>, displayLabel: string) => {
                    const isCurrentMonth = displayLabel === currentDateFormatted;
                    if (displayType === ActivePropertiesDataType.PORTFOLIO_TOTAL) {
                      if (isCurrentMonth) {
                        // Show only MTD value for current month
                        return filteredPayload.filter(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (entry: any) => entry.name === 'Month to date portfolio total',
                        );
                      } else {
                        // Show only regular value for previous months
                        return filteredPayload.filter(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (entry: any) => entry.name === 'Portfolio total',
                        );
                      }
                    } else {
                      // MARKET_AVERAGE mode
                      if (isCurrentMonth) {
                        // Show only MTD market average for current month
                        return filteredPayload.filter(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (entry: any) => entry.name === 'Month to date market total',
                        );
                      } else {
                        // Show only regular market average for previous months
                        return filteredPayload.filter(
                          // eslint-disable-next-line @typescript-eslint/no-explicit-any
                          (entry: any) => entry.name === 'Market total',
                        );
                      }
                    }
                  }
                }
              />
            }
          />
          <Legend
            wrapperStyle={{ marginBottom: '-32px' }}
            content={
              <CustomGraphLegend
                labelTypes={
                  displayType === ActivePropertiesDataType.PORTFOLIO_TOTAL
                    ? {
                        'Month to date': 'striped',
                      }
                    : {
                        'Month to date': 'striped-teal',
                      }
                }
                filterPayload={
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  (payload: Array<any>) => {
                    if (displayType === ActivePropertiesDataType.PORTFOLIO_TOTAL) {
                      // Filter out individual month-to-date entry
                      const filtered = payload.filter(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (entry: any) => entry.value !== 'Month to date portfolio total',
                      );
                      // Add a combined "Month to date" entry if month-to-date data exists
                      const hasMTD = payload.some(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (entry: any) => entry.value === 'Month to date portfolio total',
                      );
                      if (hasMTD) {
                        filtered.push({
                          value: 'Month to date',
                          type: 'rect',
                          color: '#891EFA',
                        });
                      }
                      return filtered;
                    } else {
                      // MARKET_AVERAGE mode
                      // Filter out individual month-to-date entry
                      const filtered = payload.filter(
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        (entry: any) => entry.value !== 'Month to date market total',
                      );

                      return filtered;
                    }
                  }
                }
              />
            }
          />
          {displayType === ActivePropertiesDataType.PORTFOLIO_TOTAL && (
            <>
              <Bar dataKey="Portfolio total" stackId="a" fill="#891EFA" shape={StackedBarShape} />
              <Bar
                dataKey="Month to date portfolio total"
                stackId="a"
                fill="url(#stripe-active-properties-portfolio)"
                shape={StackedBarShape}
              />
            </>
          )}
          {displayType === ActivePropertiesDataType.MARKET_TOTAL && (
            <>
              <Bar dataKey="Market total" stackId="a" fill="#009AAE" shape={StackedBarShape} />
              <Bar
                dataKey="Month to date market total"
                stackId="a"
                fill="url(#stripe-active-properties-market)"
                shape={StackedBarShape}
              />
            </>
          )}
        </BarChart>
      </ResponsiveContainer>
    </GraphWrapper>
  );
};
